/**
 * /system-admin/audit — platform-wide audit log viewer (A6.4).
 *
 * Search params: actor (UUID), action (string), from (date), to (date), page.
 * actorLabel is kept in local state (not in URL) — it is a display hint
 * derived from the actor UUID; the UUID is the source of truth.
 *
 * Note: orgId is optional on SystemAuditEntry — system-tier events have no
 * tenant context. Render a "System" badge (Globe icon) when actorUserId is
 * absent.
 */
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { ScrollText } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { STANDARD_PAGE_SIZE } from "@/components/ui/pagination";
import { SystemAdminGuard } from "@/features/system-admin/components/system-admin-guard";
import { useSystemAudit } from "@/features/system-admin/api/audit";
import { AuditFilters } from "@/features/system-admin/components/audit/audit-filters";
import { AuditTable } from "@/features/system-admin/components/audit/audit-table";

const auditSearchSchema = z.object({
  actor: z.string().optional().catch(undefined),
  action: z.string().optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  page: z.number().int().positive().optional().catch(undefined),
  pageSize: z.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute("/system-admin/audit")({
  validateSearch: auditSearchSchema.parse,
  component: SystemAdminAuditPage,
});

function SystemAdminAuditPage() {
  const t = useTranslations("system-admin.audit");
  const tc = useTranslations("system-admin.common");
  const navigate = useNavigate();
  const { actor = "", action = "", from = "", to = "", page = 1, pageSize = STANDARD_PAGE_SIZE } =
    Route.useSearch();

  // actorLabel is display-only — not persisted to URL (UUID is canonical).
  const [actorLabel, setActorLabel] = useState<string | null>(null);

  const filter = {
    actor: actor || undefined,
    action: action || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize,
  };

  const { data, isLoading, isFetching, refetch } = useSystemAudit(filter);

  const rows = data?.data ?? [];
  const total = data?.paging?.total ?? 0;

  function updateSearch(
    patch: Partial<{
      actor: string;
      action: string;
      from: string;
      to: string;
      page: number;
      pageSize: number;
    }>
  ) {
    void navigate({
      to: "/system-admin/audit",
      search: {
        actor: patch.actor ?? actor,
        action: patch.action ?? action,
        from: patch.from ?? from,
        to: patch.to ?? to,
        page: patch.page ?? 1,
        pageSize: patch.pageSize ?? pageSize,
      },
      replace: true,
    });
  }

  function handleActorSelect(id: string, label: string) {
    setActorLabel(label);
    updateSearch({ actor: id, page: 1 });
  }

  function handleActorClear() {
    setActorLabel(null);
    updateSearch({ actor: "", page: 1 });
  }

  function handleClearAll() {
    setActorLabel(null);
    void navigate({
      to: "/system-admin/audit",
      search: { actor: "", action: "", from: "", to: "", page: 1, pageSize },
      replace: true,
    });
  }

  return (
    <SystemAdminGuard>
      <div className="space-y-6">
        <PageHeader
          icon={ScrollText}
          title={tc("pages.audit.title")}
          description={tc("pages.audit.subtitle")}
          onReload={() => refetch()}
          isReloading={isFetching}
          totalLabel={total > 0 ? t("meta.found", { count: total }) : undefined}
        >
          <AuditFilters
            actorId={actor || null}
            actorLabel={actorLabel}
            action={action}
            from={from}
            to={to}
            onActorSelect={handleActorSelect}
            onActorClear={handleActorClear}
            onActionChange={(val) => updateSearch({ action: val, page: 1 })}
            onFromChange={(val) => updateSearch({ from: val, page: 1 })}
            onToChange={(val) => updateSearch({ to: val, page: 1 })}
            onClear={handleClearAll}
          />
        </PageHeader>

        <AuditTable
          data={rows}
          total={total}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => updateSearch({ page: p })}
          onPageSizeChange={(size) => updateSearch({ page: 1, pageSize: size })}
        />
      </div>
    </SystemAdminGuard>
  );
}
